// import React from 'react';
import { styled } from '@mui/material/styles';
import { Affinity, IllegalLoggingGoodsClobDto } from 'models/dtoTypes';
import SubSection from './_subSection';
import SdgScoreClob, { ISdgData } from './_sdgScoreClob';
import Empty from './_empty';
import { illegalLogging } from 'kosmos/images';
import EsgLegand from './_esgLegand';
import { smartGovernancePrimaryColor } from 'kosmosConfig';
import { useTranslation } from 'react-i18next';

const PREFIX = '_illegalLoggingReport';

const classes = {
  report: `${PREFIX}-report`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.report}`]: {},
}));

interface IProps {
  name?: string;
  illegalLoggingReport: IllegalLoggingGoodsClobDto[];
  illegalLoggingReportSmartGovernance?: IllegalLoggingGoodsClobDto[];
}

export default function IllegalLoggingGoodsReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const { illegalLoggingReport, illegalLoggingReportSmartGovernance } = props;

  const renderSingleClob = (item: IllegalLoggingGoodsClobDto, index: number) => {
    const { entitySummaryCode, entitySummaryDetail, affinity } = item;
    const secondarySgItem = illegalLoggingReportSmartGovernance?.length
      ? illegalLoggingReportSmartGovernance[index]
      : undefined;

    const secondaryAffinity = secondarySgItem?.affinity ?? Affinity.Unscored;
    const code: string = entitySummaryCode || t('unknown');
    const desc: string = entitySummaryDetail || t('unknown');
    const title = `${code} - ${desc}`;

    return (
      <SdgScoreClob
        withBorder={true}
        withBg={true}
        key={index}
        title={title}
        affinity={affinity}
        secondaryAffinity={secondaryAffinity}
      />
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'negative',
        title: t(
          'business-products-or-services-associated-with-an-elevated-risk-of-illegal-logging',
        ),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'warning',
        title: t(
          'business-products-or-services-potentially-associated-with-an-elevated-risk-of-illegal-logging',
        ),
        withBg: true,
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title={t('activity-check-against-illegal-logging')}
        learnMoreTabId="illegal-logging-check"
      />
    );
  };

  const renderHeader = () => {
    const style = {
      display: 'inline-block',
      width: 50,
      marginRight: 10,
    };
    return (
      <div>
        <div style={{ ...style, textAlign: 'center' }}>{t('auto')}</div>
        <div
          style={{
            ...style,
            color: smartGovernancePrimaryColor,
            textAlign: 'center',
          }}
        >
          {t('sg')}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (illegalLoggingReport.length === 0) {
      return <Empty title={t('no-exposure-to-illegal-logging-identified')} />;
    }

    return (
      <Root>
        {renderHeader()}
        {illegalLoggingReport.map(renderSingleClob)}
      </Root>
    );
  };

  return (
    <SubSection title={t('illegal-logging-check')} isBold icon={<img src={illegalLogging} />}>
      <>
        {renderLegands()}
        {renderContent()}
      </>
    </SubSection>
  );
}
